export function isStaticOrSystemPath(pathname: string): boolean {
  if (
    pathname.startsWith('/api') ||
    pathname.startsWith('/@') ||
    pathname.startsWith('/__') ||
    pathname.startsWith('/src') ||
    pathname.startsWith('/node_modules') ||
    pathname === '/db-visualizer.html' ||
    /\.(js|css|svg|png|jpg|jpeg|gif|webp|ico|json|woff2?|ttf|eot)$/i.test(pathname)
  ) {
    return true;
  }
  return false;
}

const PORTAL_SUBDOMAINS = ['app', 'admin', 'dev'];

/** The host without its portal label, so any deployment domain works: app.example.com -> example.com. */
export function getBaseHost(hostname: string): string {
  const host = hostname.split(':')[0].toLowerCase();
  const [first, ...rest] = host.split('.');
  return rest.length > 0 && (first === 'www' || PORTAL_SUBDOMAINS.includes(first)) ? rest.join('.') : host;
}

/** The host a portal lives on, derived from `hostname`: ('admin', 'app.example.com') -> 'admin.example.com'. */
export function getPortalHost(target: 'public' | 'app' | 'admin' | 'dev', hostname: string): string {
  const base = getBaseHost(hostname);
  return target === 'public' ? base : `${target}.${base}`;
}

export function getSubdomain(hostname: string, searchParams?: URLSearchParams): string | null {
  if (searchParams) {
    const override = searchParams.get('portal') || searchParams.get('subdomain');
    if (override && ['app', 'client', 'admin', 'dev'].includes(override)) {
      return override === 'client' ? 'app' : override;
    }
  }

  if (typeof window !== 'undefined' && !searchParams) {
    const params = new URLSearchParams(window.location.search);
    const override = params.get('portal') || params.get('subdomain');
    if (override && ['app', 'client', 'admin', 'dev'].includes(override)) {
      return override === 'client' ? 'app' : override;
    }
  }

  if (!hostname) return null;
  const host = hostname.split(':')[0].toLowerCase();

  // Ignore IP addresses & plain localhost
  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(host) || host === 'localhost') return null;

  // Local development: *.localhost (e.g. app.localhost, admin.localhost, dev.localhost)
  if (host.endsWith('.localhost')) {
    const parts = host.replace(/\.localhost$/, '').split('.');
    const sub = parts[parts.length - 1];
    return sub && sub !== 'www' ? sub : null;
  }

  // Deployed: <portal>.<any domain> (e.g. app.mybusinessmate.ai)
  const first = host.split('.')[0];
  if (host.includes('.') && PORTAL_SUBDOMAINS.includes(first)) return first;

  return null;
}

export function buildSubdomainUrl(targetSub: 'public' | 'app' | 'admin' | 'dev', path = '/'): string {
  const isClient = typeof window !== 'undefined';
  const host = isClient ? window.location.hostname : 'localhost';
  const isLocal = host.endsWith('localhost') || host === 'localhost';
  const port = isClient && window.location.port ? `:${window.location.port}` : '';
  const protocol = isClient ? window.location.protocol : 'http:';

  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  return `${isLocal ? protocol : 'https:'}//${getPortalHost(targetSub, host)}${isLocal ? port : ''}${cleanPath}`;
}

export function ensureSubdomain(expectedSubdomain: string) {
  return () => {
    if (typeof window === 'undefined') return;
    const currentSub = getSubdomain(window.location.hostname);
    if (currentSub !== expectedSubdomain) {
      const targetUrl = buildSubdomainUrl(
        expectedSubdomain as 'public' | 'app' | 'admin' | 'dev',
        window.location.pathname.replace(`/subdomain/${expectedSubdomain}`, '') || '/',
      );
      window.location.replace(targetUrl);
    }
  };
}

export interface LocationRewrite {
  input: (opts: { url: URL }) => URL;
  output: (opts: { url: URL }) => URL;
}

export const applySubdomainRewrite: LocationRewrite = {
  // 1. INCOMING REQUEST (Maps Host to Internal File Route)
  input: ({ url }: { url: URL }) => {
    if (isStaticOrSystemPath(url.pathname)) return url;

    const sub = getSubdomain(url.hostname, url.searchParams);
    if (sub) {
      const prefix = `/subdomain/${sub}`;
      if (url.pathname === '/' || url.pathname === '') {
        url.pathname = prefix;
      } else if (!url.pathname.startsWith(prefix)) {
        url.pathname = `${prefix}${url.pathname}`;
      }
      return url;
    }
    return url;
  },

  // 2. OUTGOING NAVIGATION (Keeps rendered links clean and resolves domains)
  output: ({ url }: { url: URL }) => {
    const currentHost = typeof window !== 'undefined' ? window.location.hostname : url.hostname;
    const currentSub = getSubdomain(currentHost);
    const isLocal = currentHost.endsWith('localhost') || currentHost === 'localhost';
    const port = typeof window !== 'undefined' && window.location.port ? `:${window.location.port}` : '';
    const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';

    // Case 1: Link targets a subdomain route (/subdomain/<subdomain>/*)
    if (url.pathname.startsWith('/subdomain/')) {
      const segments = url.pathname.split('/').filter(Boolean);
      const targetSub = segments[1];
      const remaining = segments.slice(2).join('/');

      // If we are ALREADY on this exact subdomain, keep it local with a clean path
      if (currentSub && targetSub === currentSub) {
        url.pathname = remaining ? `/${remaining}` : '/';
        return url;
      }

      // If we are on a DIFFERENT subdomain OR on the APEX domain, rewrite to target origin
      if (targetSub) {
        url.protocol = isLocal ? protocol : 'https:';
        url.host = `${getPortalHost(targetSub as 'app' | 'admin' | 'dev', currentHost)}${isLocal ? port : ''}`;
        url.pathname = remaining ? `/${remaining}` : '/';
        return url;
      }
    }

    // Case 2: We are on a subdomain, but the link is an apex route (e.g. /about, /pricing, /)
    if (currentSub && !url.pathname.startsWith('/subdomain/')) {
      url.protocol = isLocal ? protocol : 'https:';
      url.host = `${getBaseHost(currentHost)}${isLocal ? port : ''}`;
      return url;
    }

    return url;
  },
};
